import React, { useState } from "react";
import axios from "axios";
import { KeyRound, CheckCircle2, UserCheck } from "lucide-react";
import { API_BASE, setPwaSession } from "../lib/api";

export default function PwaEnroll({ employees, fingerprint, onEnrolled, onCancel }) {
  const [step, setStep] = useState(1);
  const [emp, setEmp] = useState(null);
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setError("");
    setBusy(true);
    try {
      const r = await axios.post(`${API_BASE}/presence/pwa/enroll`, {
        employee_id: emp.id,
        pin,
        device_fingerprint: fingerprint,
      });
      setPwaSession(r.data.pwa_device_token, {
        employee: r.data.employee,
        outlet: r.data.outlet,
        expires_at_iso: r.data.expires_at_iso,
        fingerprint,
      });
      onEnrolled(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Enrolment failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-4" data-testid="pwa-enroll">
      <div className="mono-label">DEVICE ENROLMENT</div>
      <h2 className="font-heading text-2xl font-black tracking-tighter mt-2">
        Pair this device to your identity.
      </h2>
      <p className="text-neutral-600 mt-2 text-sm leading-relaxed">
        Enrolment issues a short-lived token (24&nbsp;hours) that authorises offline punches from this
        device. Only you can enrol yourself — your PIN never leaves the server hashed.
      </p>

      {step === 1 && (
        <>
          <div className="mono-label mt-6">SELECT YOURSELF</div>
          <div className="mt-3 space-y-2">
            {employees.map((e) => (
              <button
                key={e.id}
                data-testid={`enroll-pick-${e.code}`}
                onClick={() => { setEmp(e); setStep(2); }}
                className="w-full text-left border border-neutral-200 bg-white p-3 flex items-center gap-3 hover:border-black"
              >
                <img src={e.photo_url} alt="" className="w-12 h-12 object-cover grayscale border border-neutral-200" />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{e.name}</div>
                  <div className="mono-label" style={{ fontSize: 9 }}>{e.code} · {e.role}</div>
                </div>
                <UserCheck size={18} className="text-neutral-400" />
              </button>
            ))}
          </div>
          <button onClick={onCancel} className="mt-4 h-11 px-4 border border-neutral-300 font-mono text-xs uppercase tracking-wider w-full">
            Cancel
          </button>
        </>
      )}

      {step === 2 && (
        <>
          <div className="mt-6 flex items-center gap-3 border border-neutral-200 bg-[#FAFAFA] p-3">
            <img src={emp.photo_url} alt="" className="w-12 h-12 object-cover grayscale border border-neutral-200" />
            <div>
              <div className="font-semibold">{emp.name}</div>
              <div className="mono-label" style={{ fontSize: 9 }}>{emp.code}</div>
            </div>
            <button onClick={() => setStep(1)} className="ml-auto mono-label hover:underline">Change</button>
          </div>
          <label className="mono-label block mt-4">Your PIN</label>
          <input
            data-testid="enroll-pin"
            type="password"
            inputMode="numeric"
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 8))}
            className="w-full mt-2 h-14 px-4 border border-neutral-300 bg-white font-mono tracking-[0.5em] text-lg focus:outline-none focus:border-black"
            placeholder="••••"
            autoFocus
          />
          <div className="mt-3 mono-label inline-flex items-center gap-1" style={{ fontSize: 10 }}>
            <KeyRound size={12} /> Fingerprint · <span className="opacity-60">{fingerprint.slice(0, 18)}…</span>
          </div>
          {error && <div data-testid="enroll-error" className="mt-3 text-red-700 font-mono text-sm">{error}</div>}
          <button
            data-testid="btn-enroll-submit"
            onClick={submit}
            disabled={!pin || busy}
            className="mt-4 w-full h-14 bg-black text-white font-mono text-sm uppercase tracking-wider disabled:opacity-50 inline-flex items-center justify-center gap-2"
          >
            <CheckCircle2 size={16} /> {busy ? "Enrolling…" : "Enrol device (24h token)"}
          </button>
        </>
      )}
    </div>
  );
}
